import { Dialog, DialogContent, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ModalActionBar from 'components/modal-action-bar';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchContact from 'views/orders/new-charges/components/searchContact';

const useStyles = makeStyles((theme: any) => ({
  content: {
    padding: theme.spacing(3),
  },
  title: {
    fontWeight: 500,
    color: theme.palette.primary.dark,
    marginBottom: theme.spacing(1),
  },
  text: {
    color: theme.palette.tab.offselected,
    marginBottom: theme.spacing(2),
  },
  warning: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
}));

type Contact = { _id: string; name: string };

type Props = {
  open: boolean;
  onClose: () => void;
  onAdd: (partyId: string, onSuccess: () => void) => void;
  isSubmitting: boolean;
  existingLenderIds: string[];
};

export const AddLenderModal: FC<Props> = ({
  open,
  onClose,
  onAdd,
  isSubmitting,
  existingLenderIds,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Contact | null>(null);
  const isAlreadyLender = !!selected && existingLenderIds.includes(selected._id);

  const resetAndClose = () => {
    setSelected(null);
    onClose();
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetAndClose();
  };

  const handleSubmit = () => {
    if (selected && !isAlreadyLender && !isSubmitting) {
      onAdd(selected._id, resetAndClose);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogContent className={classes.content}>
        <Typography variant="h6" className={classes.title}>
          {t('loan:add-lender-title')}
        </Typography>
        <Typography variant="body2" className={classes.text}>
          {t('loan:add-lender-text')}
        </Typography>
        <SearchContact
          getValue={(contact) => setSelected(contact ? { _id: contact._id, name: contact.name } : null)}
          reference={null}
          initialValue={{ name: '', _id: '' }}
          disabled={isSubmitting}
          onTextInput={() => setSelected(null)}
        />
        {isAlreadyLender && (
          <Typography variant="body2" className={classes.warning}>
            {t('loan:lender-already-added')}
          </Typography>
        )}
      </DialogContent>
      <ModalActionBar
        onCancel={handleClose}
        submitText={t('loan:add-lender')}
        submitType="button"
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitDisabled={!selected || isAlreadyLender}
      />
    </Dialog>
  );
};

export default AddLenderModal;
